import type { SVGProps } from "react";
import { useTranslation } from "react-i18next";
import type { TooltipProps } from "recharts";
import { mirrorChartMargin } from "src/components/charts/chartRtl";
import {
  formatNumber,
  formatNumberDigits,
  isArabicLanguage,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";
import { formatOrganicPostTypeLabel } from "../organicMediaDefinitions";
import type { SocialValueFormat } from "./socialReportConfig";

/**
 * The chart tooltips, axis tick and percent-axis helper used by the organic
 * content reports, kept in one place so the TikTok and Snapchat views render and
 * hover exactly like the Facebook and Instagram ones.
 */

const isPercent = (format: SocialValueFormat) => format === "percent";

export const formatSocialMetricTick = (
  value: number | string,
  format: SocialValueFormat,
  language: string
): string => {
  const numeric = Number(value ?? 0);
  if (isPercent(format)) {
    return wrapBidiIsolate(
      `${formatNumber(numeric, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, language)}%`
    );
  }
  return formatNumber(numeric, {}, language);
};

/** DD-MM-YYYY for daily buckets, MM-YYYY for monthly — as the other reports do. */
export const formatSocialTrendDate = (
  value: string | number,
  language = "en"
): string => {
  const raw = String(value ?? "");
  let formatted = raw;
  const monthMatch = raw.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    formatted = `${monthMatch[2]}-${monthMatch[1]}`;
  } else {
    const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    formatted = isoMatch ? `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}` : raw;
  }
  return isArabicLanguage(language) ? toArabicIndicDigitsInString(formatted) : formatted;
};

/** SVG axis ticks — locale digits without bidi isolate (isolate chars can break SVG text). */
export const formatSocialChartAxisCount = (value: number | string, language: string): string =>
  formatNumberDigits(Number(value ?? 0), {}, language);

export const formatSocialChartAxisPercent = (value: number | string, language: string): string =>
  formatNumberDigits(Number(value ?? 0), { minimumFractionDigits: 0, maximumFractionDigits: 2 }, language);

/** Reserve gutter space so dual Y-axis ticks sit outside the plot area. */
export const getSocialTrendChartMargin = (isRTL: boolean) =>
  mirrorChartMargin({ top: 8, right: 4, left: 4, bottom: 4 }, isRTL);

export const SOCIAL_TREND_Y_AXIS = {
  pctWidth: 36,
  countWidth: 56,
  tickMargin: 4,
} as const;

type SocialTrendAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: number };
};

/** Left / percent axis — anchor labels outside the plot on the left. */
export const renderSocialTrendPercentAxisTick =
  (language: string) =>
  (props: SocialTrendAxisTickProps) => {
    const { x = 0, y = 0, payload } = props;

    return (
      <text
        x={x}
        y={y}
        dy={4}
        dx={-4}
        textAnchor="end"
        direction="ltr"
        fill="#5e6a7d"
        fontSize={12}
      >
        {formatSocialChartAxisPercent(payload?.value ?? 0, language)}
      </text>
    );
  };

/** Right / count axis — anchor labels outside the plot on the right. */
export const renderSocialTrendCountAxisTick =
  (language: string) =>
  (props: SocialTrendAxisTickProps) => {
    const { x = 0, y = 0, payload } = props;

    return (
      <text
        x={x}
        y={y}
        dy={4}
        dx={4}
        textAnchor="start"
        direction="ltr"
        fill="#5e6a7d"
        fontSize={12}
      >
        {formatSocialChartAxisCount(payload?.value ?? 0, language)}
      </text>
    );
  };

/** Bottom gutter for content-type bar charts — room for X-axis category labels. */
export const getSocialBarChartMargin = (isRTL: boolean) =>
  mirrorChartMargin({ top: 24, right: 12, left: 12, bottom: 28 }, isRTL);

export const SOCIAL_BAR_X_AXIS_HEIGHT = 40;

export interface SocialPercentAxisConfig {
  domain?: [number, number];
  ticks?: number[];
}

export function getSocialPercentAxisConfig(values: number[]): SocialPercentAxisConfig {
  const maxValue = Math.max(0, ...values.map(value => Number(value ?? 0)));
  const step = maxValue <= 2 ? 0.5 : maxValue <= 5 ? 1 : 2;
  const upperBound = Math.max(step, Math.ceil(maxValue / step) * step);
  const tickCount = Math.max(2, Math.round(upperBound / step));

  return {
    domain: [0, upperBound],
    ticks: Array.from({ length: tickCount + 1 }, (_unused, index) =>
      Number((index * step).toFixed(2))
    ),
  };
}

export function SocialDonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const slice = payload[0];
  const percent = (slice as { percent?: number }).percent;
  const typeKey = (slice.payload as { typeKey?: string } | undefined)?.typeKey;

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.social.contentType")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {formatOrganicPostTypeLabel(String(typeKey ?? slice.name), t)}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.social.totalContent")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {formatNumber(Number(slice.value), {}, i18n.language)}
            {typeof percent === "number"
              ? wrapBidiIsolate(
                  ` (${formatNumber(percent * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%)`
                )
              : ""}
          </strong>
        </div>
      </div>
    </div>
  );
}

export function SocialBarTooltip({
  active,
  payload,
  format,
}: TooltipProps<number, string> & { format: SocialValueFormat }) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as { typeKey?: string } | undefined;

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.social.contentType")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {row?.typeKey ? formatOrganicPostTypeLabel(row.typeKey, t) : "-"}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.tooltips.metricValue")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {formatSocialMetricTick(Number(payload[0]?.value ?? 0), format, i18n.language)}
          </strong>
        </div>
      </div>
    </div>
  );
}

export function SocialTrendTooltip({
  active,
  label,
  payload,
  formats,
}: TooltipProps<number, string> & { formats: Record<string, SocialValueFormat> }) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.tooltips.date")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {formatSocialTrendDate(String(label ?? ""), i18n.language)}
          </strong>
        </div>
        {payload.map(entry => (
          <div className="organic-tooltip__meta-line" key={String(entry.dataKey)}>
            <span className="organic-tooltip__meta-label">{String(entry.name)}</span>
            <strong className="organic-tooltip__meta-value">
              {formatSocialMetricTick(
                Number(entry.value ?? 0),
                formats[String(entry.dataKey)] ?? "count",
                i18n.language
              )}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export function SocialContentTypeAxisTick(
  props: SVGProps<SVGTextElement> & { x?: number; y?: number; payload?: { value: string } }
) {
  const { t } = useTranslation();
  const { x = 0, y = 0, payload } = props;
  const words = formatOrganicPostTypeLabel(payload?.value ?? "", t)
    .split(/\s+/)
    .filter(Boolean);

  return (
    <g transform={`translate(${x},${y + 6})`}>
      <text x={0} y={0} dy={0} textAnchor="middle" fill="#334155" fontSize={12}>
        {words.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}
